import {pluralize} from "@rw/string_utils";

/** Polish plural for "ogłoszenie": 1 / 2–4 / 5+. */
export const pluralListing = pluralize(["ogłoszenie", "ogłoszenia", "ogłoszeń"]);

export const LISTING_SITE_NAME = "zakupauto.pl";

export interface IListingHeadingInput {
    lead: "Samochody używane" | "Auta używane";
    makeModel?: string;
    place?: string;
    count: number;
    page: number;
    /** When set, appended once as `| {siteName}`. Do not also add the global ` - zakupauto.pl` suffix. */
    siteName?: string;
}

export const buildUsedCarsHeading = ({lead, makeModel, place, count, page, siteName}: IListingHeadingInput): string => {
    const head = [lead, makeModel, place].filter(Boolean).join(" ");
    const countSuffix = count > 0 ? ` – ${count} ${pluralListing(count)}` : "";
    const pageSuffix = page > 1 ? ` – strona ${page}` : "";
    const siteSuffix = siteName ? ` | ${siteName}` : "";
    return `${head}${countSuffix}${pageSuffix}${siteSuffix}`;
};

export const buildCarListH1 = (input: Omit<IListingHeadingInput, "lead" | "siteName">): string => buildUsedCarsHeading({...input, lead: "Samochody używane"});

export const buildCarListDocumentTitle = (input: Omit<IListingHeadingInput, "lead" | "siteName">): string =>
    buildUsedCarsHeading({...input, lead: "Auta używane", siteName: LISTING_SITE_NAME});

/** Self-canonical. Page 1 has no query. Later pages keep `?page=N` and the same path. */
export const buildCarListCanonical = (siteBase: string, listPath: string, page: number): string => {
    const base = siteBase.replace(/\/+$/, "");
    const path = listPath.startsWith("/") ? listPath : `/${listPath}`;
    return page > 1 ? `${base}${path}?page=${page}` : `${base}${path}`;
};

/** Empty lists and page 11+ stay noindex. */
export const buildCarListRobots = (offersCount: number, page: number): "index, follow" | "noindex, follow" => {
    if (offersCount > 0 && page < 11) {
        return "index, follow";
    }
    return "noindex, follow";
};

export interface IIntroCity {
    city: string;
    citySlug: string;
    offerCount: number;
}

/**
 * One short city paragraph from counts we already have.
 * Returns null when it would only repeat the H1 or when there are no offers.
 */
export const buildCityListingIntro = (input: {
    count: number;
    cityName?: string;
    citySlug?: string;
    voivodeshipName?: string;
    makeModel?: string;
    otherCities?: IIntroCity[];
}): string | null => {
    const cityName = input.cityName?.trim();
    if (!cityName || input.count <= 0) {
        return null;
    }

    const subject = input.makeModel ? `samochodów używanych ${input.makeModel}` : "samochodów używanych";
    const sentences = [`${input.count} ${pluralListing(input.count)} ${subject} w miejscowości ${cityName}.`];

    const voivodeshipName = input.voivodeshipName?.trim();
    if (voivodeshipName) {
        sentences.push(`Województwo ${voivodeshipName}.`);
    }

    const currentSlug = input.citySlug?.trim().toLowerCase();
    const others = (input.otherCities || [])
        .filter((city) => city.offerCount > 0 && city.city.trim() && city.citySlug.trim().toLowerCase() !== currentSlug)
        .sort((a, b) => b.offerCount - a.offerCount || a.city.localeCompare(b.city, "pl"))
        .slice(0, 4);

    if (others.length > 0) {
        const listed = others.map((city) => `${city.city} (${city.offerCount})`).join(", ");
        sentences.push(`Inne miasta z ogłoszeniami: ${listed}.`);
    }

    if (!voivodeshipName && others.length === 0) {
        return null;
    }

    return sentences.join(" ");
};

export const buildLocationMetaDescription = (input: {count: number; placePhrase?: string; makeModel?: string; page: number}): string => {
    const pageNote = input.page > 1 ? ` Strona ${input.page}.` : "";
    const subject = input.makeModel ? `samochodów używanych ${input.makeModel}` : "samochodów używanych";

    if (!input.placePhrase) {
        return `Ogłoszenia ${subject} na zakupauto.pl.${pageNote}`;
    }
    if (input.count <= 0) {
        return `Brak ogłoszeń ${subject} w ${input.placePhrase}.${pageNote}`;
    }
    return `${input.count} ${pluralListing(input.count)} ${subject} w ${input.placePhrase}.${pageNote}`;
};
